import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux/common/store';
import { useCreateTaskMutation } from '@/redux/internal-services/workspaceApiExtensions';
import type { CreateTaskInput, ContextLink } from '@/redux/internal-services/workspaceApiExtensions';
import { useListTenantUsersQuery, useListAssignableRolesQuery } from '@/redux/internal-services/onboardingApi';
import { useGetMeetingsQuery, useGetPropertiesQuery } from '@/redux/internal-services/workspaceApi';
import { Button } from '@/components/UI/Button';
import { PriorityIcon } from '@/components/Tasks/TaskCard';

const labelCls = 'block text-sm font-medium text-ui-text mb-1';

const CONTEXT_TYPES = ['property', 'meeting'];

// ── Custom Date Picker ────────────────────────────────────────────────────────
const CAL_DAYS   = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const CAL_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const CustomDatePicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  const parsed = value ? new Date(value + 'T00:00') : null;
  const [calYear,  setCalYear]  = useState(parsed?.getFullYear()  ?? today.getFullYear());
  const [calMonth, setCalMonth] = useState(parsed?.getMonth()     ?? today.getMonth());

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const daysInMonth = (m: number, y: number) => new Date(y, m + 1, 0).getDate();
  const firstDay    = new Date(calYear, calMonth, 1).getDay();
  const totalDays   = daysInMonth(calMonth, calYear);
  const cells       = [...Array(firstDay).fill(null), ...Array.from({ length: totalDays }, (_, i) => i + 1)];

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); };

  const pickDay = (day: number) => {
    const d = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onChange(d);
    setOpen(false);
  };

  const display = parsed
    ? parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Pick date';

  return (
    <div ref={wrapRef} className="relative flex-1">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-colors ${
          value ? 'border-accent/30 bg-accent-soft/10' : 'border-ui-border bg-ui-subtle hover:border-ui-border'
        }`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className={value ? 'text-accent-text' : 'text-ui-text3'}>
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <span className={`text-sm font-medium ${value ? 'text-ui-text' : 'text-ui-text3'}`}>{display}</span>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 bg-ui-card border border-ui-border rounded-2xl shadow-lg p-4 z-50 w-72">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-ui-text3 hover:bg-ui-subtle hover:text-ui-text transition-colors">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span className="text-sm font-semibold text-ui-text">{CAL_MONTHS[calMonth]} {calYear}</span>
            <button type="button" onClick={nextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-ui-text3 hover:bg-ui-subtle hover:text-ui-text transition-colors">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {CAL_DAYS.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-ui-text3 py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((day, i) => {
              if (!day) return <div key={i} />;
              const isToday = day === today.getDate() && calMonth === today.getMonth() && calYear === today.getFullYear();
              const isSel   = parsed && day === parsed.getDate() && calMonth === parsed.getMonth() && calYear === parsed.getFullYear();
              return (
                <button key={i} type="button" onClick={() => pickDay(day)}
                  className={`mx-auto flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                    isSel    ? 'bg-accent-fill text-on-accent font-bold'
                    : isToday ? 'border border-accent text-accent-text font-semibold'
                    :           'text-ui-text hover:bg-accent-soft/40'
                  }`}>
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

// ── Custom Time Picker ────────────────────────────────────────────────────────
function parseTime(value: string): { hour: number; minute: number; ampm: 'AM' | 'PM' } {
  if (!value) return { hour: 12, minute: 0, ampm: 'PM' };
  const [h, m] = value.split(':').map(Number);
  return { hour: h % 12 || 12, minute: Math.floor(m / 5) * 5, ampm: h >= 12 ? 'PM' : 'AM' };
}

function toTimeString(hour: number, minute: number, ampm: 'AM' | 'PM'): string {
  const h24 = ampm === 'AM' ? (hour === 12 ? 0 : hour) : (hour === 12 ? 12 : hour + 12);
  return `${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

const CustomTimePicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { hour, minute, ampm } = parseTime(value);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const set = (h: number, m: number, ap: 'AM' | 'PM') => onChange(toTimeString(h, m, ap));

  const ChevBtn: React.FC<{ dir: 'up' | 'down'; onClick: () => void }> = ({ dir, onClick }) => (
    <button type="button" onClick={onClick}
      className="w-8 h-7 flex items-center justify-center rounded-lg text-ui-text3 hover:bg-ui-subtle hover:text-ui-text transition-colors">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        {dir === 'up' ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
      </svg>
    </button>
  );

  const display = value
    ? new Date(`2000-01-01T${value}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    : 'Pick time';

  return (
    <div ref={wrapRef} className="relative flex-1">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-colors ${
          value ? 'border-accent/30 bg-accent-soft/10' : 'border-ui-border bg-ui-subtle hover:border-ui-border'
        }`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className={value ? 'text-accent-text' : 'text-ui-text3'}>
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
        <span className={`text-sm font-medium ${value ? 'text-ui-text' : 'text-ui-text3'}`}>{display}</span>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 bg-ui-card border border-ui-border rounded-2xl shadow-lg p-4 z-50 w-full min-w-[200px]">
          <div className="flex items-center justify-center gap-3">
            {/* Hour */}
            <div className="flex flex-col items-center gap-1">
              <ChevBtn dir="up"   onClick={() => set(hour === 12 ? 1 : hour + 1, minute, ampm)} />
              <span className="text-h2 text-ui-text w-10 text-center tabular-nums">
                {String(hour).padStart(2, '0')}
              </span>
              <ChevBtn dir="down" onClick={() => set(hour === 1 ? 12 : hour - 1, minute, ampm)} />
            </div>

            <span className="text-h2 text-ui-text3 pb-0.5 select-none">:</span>

            {/* Minute */}
            <div className="flex flex-col items-center gap-1">
              <ChevBtn dir="up"   onClick={() => set(hour, (minute + 5) % 60, ampm)} />
              <span className="text-h2 text-ui-text w-10 text-center tabular-nums">
                {String(minute).padStart(2, '0')}
              </span>
              <ChevBtn dir="down" onClick={() => set(hour, minute === 0 ? 55 : minute - 5, ampm)} />
            </div>

            {/* AM / PM */}
            <div className="flex flex-col gap-1.5 ml-1">
              {(['AM', 'PM'] as const).map((ap) => (
                <button key={ap} type="button" onClick={() => set(hour, minute, ap)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                    ampm === ap ? 'bg-accent-fill text-on-accent' : 'bg-ui-subtle text-ui-text3 hover:bg-ui-hover hover:text-ui-text'
                  }`}>
                  {ap}
                </button>
              ))}
            </div>
          </div>

          <button type="button" onClick={() => { if (!value) onChange(toTimeString(hour, minute, ampm)); setOpen(false); }}
            className="mt-4 w-full py-2 rounded-xl btn-sheen text-on-primary text-sm font-semibold">
            Done
          </button>
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

export interface TaskFormProps {
  /** Prefill values (e.g. when converting a note follow-up into a task). */
  initial?: Partial<{ title: string; taskType: string; priority: string; dueDate: string; tags: string[] }>;
  submitLabel?: string;
  /** Called after a successful create instead of navigating to the task page. */
  onCreated?: (task: { task_id: string }) => void;
  /** Called by Cancel instead of navigating to the tasks list. */
  onCancel?: () => void;
  /** Override how the task is created (e.g. route through the note convert endpoint). */
  createOverride?: (body: CreateTaskInput) => Promise<{ task_id: string }>;
  /** Inline-edit mode: hide the Create/Cancel row — the form is just editable fields, nothing
   *  is submitted here. Pair with `onFieldsChange` so the caller owns when/how it's created. */
  hideActions?: boolean;
  /** Fires with the current field values on every change (inline-edit mode). */
  onFieldsChange?: (body: CreateTaskInput) => void;
}

const TaskForm: React.FC<TaskFormProps> = ({ initial, submitLabel, onCreated, onCancel, createOverride, hideActions, onFieldsChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createTask, { isLoading }] = useCreateTaskMutation();

  const [title, setTitle] = useState(initial?.title ?? '');
  const [taskType, setTaskType] = useState(initial?.taskType ?? 'general');
  const [priority, setPriority] = useState(initial?.priority ?? 'normal');
  const [dueDate, setDueDate]   = useState(initial?.dueDate ?? ''); // YYYY-MM-DD
  const [dueTime, setDueTime]   = useState(''); // HH:MM
  const [error, setError]       = useState('');

  // Current user
  const currentUserId   = useSelector((s: RootState) => s.sessionState.userId)   ?? '';
  const currentUserName = useSelector((s: RootState) => s.sessionState.userName)  ?? '';

  // Assignment
  const [assignmentMode, setAssignmentMode] = useState<'self' | 'user' | 'queue'>('self');
  const [showAssignPanel, setShowAssignPanel] = useState(false);
  const [assignTab, setAssignTab]             = useState<'user' | 'role'>('user');
  const [assigneeSearch, setAssigneeSearch]   = useState('');
  const [roleSearch, setRoleSearch]           = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState<{ id: string; label: string } | null>(null);
  const [queueRoleId,   setQueueRoleId]   = useState('');
  const [queueRoleName, setQueueRoleName] = useState('');

  const { data: usersData }  = useListTenantUsersQuery({ search: assigneeSearch || undefined, page_size: 8 }, { skip: !showAssignPanel || assignTab !== 'user' });
  const { data: rolesData }  = useListAssignableRolesQuery(undefined, { skip: !showAssignPanel || assignTab !== 'role' });

  // Context link
  const [contextType, setContextType]       = useState('');
  const [contextEntityId, setContextEntityId] = useState('');
  const [contextLabel, setContextLabel]     = useState('');
  const [meetingSearch, setMeetingSearch]   = useState('');
  const [propertySearch, setPropertySearch] = useState('');
  const { data: meetingsData } = useGetMeetingsQuery(undefined, { skip: contextType !== 'meeting' });
  const { data: propertiesData } = useGetPropertiesQuery(
    propertySearch ? { search: propertySearch } : undefined,
    { skip: contextType !== 'property' },
  );

  // Tags
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  const buildBody = (): CreateTaskInput => {
    const context: ContextLink | undefined = contextType && contextEntityId
      ? { context_type: contextType, entity_id: contextEntityId, display_label: contextLabel || undefined }
      : undefined;

    return {
      title: title.trim(),
      task_type: taskType,
      priority,
      due_date: dueDate ? (dueTime ? `${dueDate}T${dueTime}` : dueDate) : undefined,
      assignee_user_id: assignmentMode === 'self' ? currentUserId || undefined : assignmentMode === 'user' ? selectedAssignee?.id : undefined,
      queue_role_id: assignmentMode === 'queue' ? queueRoleId || undefined : undefined,
      context,
      tags: tags.length > 0 ? tags : undefined,
    };
  };

  // Inline-edit mode: report the current field values up on every change instead of waiting
  // for a submit — there is no submit button in this mode.
  useEffect(() => {
    if (!hideActions) return;
    onFieldsChange?.(buildBody());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hideActions, title, taskType, priority, dueDate, dueTime,
    assignmentMode, selectedAssignee, queueRoleId, contextType, contextEntityId, contextLabel, tags,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hideActions) return; // inline-edit mode has no submit action — Enter shouldn't create
    if (!title.trim()) { setError(t('tasks.titleRequired')); return; }

    const body = buildBody();

    try {
      const result = createOverride ? await createOverride(body) : await createTask(body).unwrap();
      if (onCreated) onCreated(result);
      else navigate(`/tasks/${result.task_id}`);
    } catch (err: unknown) {
      const msg = (err as { data?: { message?: string } })?.data?.message || 'Failed to create task.';
      setError(msg);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl">
      {error && <p className="text-sm text-danger-text">{error}</p>}

      <div className="relative bg-ui-subtle rounded-2xl px-4 py-4">
        <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-ui-hover text-xs font-medium text-ui-text3">
          draft
        </span>
        <textarea
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('tasks.titlePlaceholder', 'Task title...')}
          rows={2}
          maxLength={500}
          className="w-full text-lg font-semibold text-ui-text placeholder:text-ui-text3/50 bg-transparent outline-none resize-none leading-snug font-poppins pr-12"
          required
        />
      </div>

      <div className="space-y-3">
        <div>
          <label className={labelCls}>{t('tasks.type', 'Type')}</label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {[
              { value: 'follow_up', label: 'Follow up',  dot: 'bg-accent',   active: 'bg-accent/10 border-accent text-accent-text' },
              { value: 'approval',  label: 'Approval',  dot: 'bg-success',  active: 'bg-success/10 border-success text-success-text' },
              { value: 'review',    label: 'Review',    dot: 'bg-accent', active: 'bg-accent/10 border-accent text-accent-text' },
            ].map((t2) => (
              <button
                key={t2.value}
                type="button"
                onClick={() => setTaskType(taskType === t2.value ? 'general' : t2.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  taskType === t2.value
                    ? t2.active
                    : 'border-ui-border bg-ui-subtle text-ui-text2 hover:border-ui-border'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${t2.dot}`} />
                {t2.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls}>{t('tasks.priority', 'Priority')}</label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {[
              { value: 'low',    label: 'Low',    dot: 'bg-steel',  active: 'bg-ui-hover border-ui-border3 text-ui-text' },
              { value: 'normal', label: 'Normal', dot: 'bg-accent',  active: 'bg-accent/10 border-accent text-accent-text' },
              { value: 'high',   label: 'High',   dot: 'bg-warning', active: 'bg-warning/10 border-warning text-warning-text' },
              { value: 'urgent', label: 'Urgent', dot: 'bg-danger',   active: 'bg-danger/10 border-danger text-danger-text' },
            ].map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPriority(p.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  priority === p.value
                    ? p.active
                    : 'border-ui-border bg-ui-subtle text-ui-text2 hover:border-ui-border'
                }`}
              >
                <PriorityIcon level={p.value} size={10} />
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls}>{t('tasks.dueDate', 'Due Date')}</label>
          <div className="flex gap-2 mt-1">
            <CustomDatePicker value={dueDate} onChange={setDueDate} />

            <CustomTimePicker value={dueTime} onChange={setDueTime} />
          </div>
        </div>
      </div>

      {/* ── Assignment ── */}
      <div>
        <label className={labelCls}>Assigned to</label>

        {/* Current assignee card */}
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-ui-border bg-ui-subtle">
          <div className="w-8 h-8 rounded-full bg-accent-fill flex items-center justify-center text-on-accent text-sm font-bold flex-shrink-0">
            {assignmentMode === 'self'  ? (currentUserName || 'Y')[0].toUpperCase()
            : assignmentMode === 'user' ? (selectedAssignee?.label || 'U')[0].toUpperCase()
            : '⚙'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ui-text truncate">
              {assignmentMode === 'self'  ? (currentUserName || 'You')
              : assignmentMode === 'user' ? selectedAssignee?.label
              : (queueRoleName || 'Role queue')}
            </p>
            <p className="text-xs text-ui-text3">
              {assignmentMode === 'self' ? 'Assigned to you' : assignmentMode === 'queue' ? 'Role queue' : 'Team member'}
            </p>
          </div>
          <button type="button" onClick={() => setShowAssignPanel(o => !o)}
            className="text-xs font-semibold text-accent-text hover:opacity-70 transition-opacity flex-shrink-0">
            Change
          </button>
        </div>

        {/* Dropdown panel */}
        {showAssignPanel && (
          <div className="mt-1.5 border border-ui-border rounded-xl overflow-hidden bg-ui-card shadow-sm">
            {/* Tabs */}
            <div className="flex border-b border-ui-border">
              {(['user', 'role'] as const).map((tab) => (
                <button key={tab} type="button" onClick={() => { setAssignTab(tab); setAssigneeSearch(''); setRoleSearch(''); }}
                  className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
                    assignTab === tab ? 'text-accent-text border-b-2 border-accent bg-accent-soft/10' : 'text-ui-text3 hover:text-ui-text'
                  }`}>
                  {tab === 'user' ? 'Assign to user' : 'Role queue'}
                </button>
              ))}
            </div>

            {assignTab === 'user' && (
              <div>
                {/* Inline search row */}
                <div className="flex items-center gap-2 px-3 py-2 border-b border-ui-border">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ui-text3 flex-shrink-0">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input value={assigneeSearch} onChange={(e) => setAssigneeSearch(e.target.value)}
                    placeholder="Search team members…"
                    className="flex-1 text-sm text-ui-text bg-transparent outline-none placeholder:text-ui-text3" />
                </div>

                {/* Self option */}
                <button type="button"
                  onClick={() => { setAssignmentMode('self'); setSelectedAssignee(null); setShowAssignPanel(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left border-b border-ui-border transition-colors ${assignmentMode === 'self' ? 'bg-accent-soft/20' : 'hover:bg-ui-subtle'}`}>
                  <div className="w-7 h-7 rounded-full bg-accent-fill flex items-center justify-center text-on-accent text-xs font-bold flex-shrink-0">
                    {(currentUserName || 'Y')[0].toUpperCase()}
                  </div>
                  <span className="text-sm text-ui-text flex-1">{currentUserName || 'You'} <span className="text-ui-text3 font-normal">(you)</span></span>
                  {assignmentMode === 'self' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-accent-text flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>

                {(usersData?.data ?? []).filter(u => u.user_id !== currentUserId).length > 0 && (
                  <div className="max-h-36 overflow-y-auto border-t border-ui-border">
                    {(usersData?.data ?? []).filter(u => u.user_id !== currentUserId).map((u) => {
                      const label = u.name || u.email || t('common.unknownUser', 'Unknown');
                      const isSel = selectedAssignee?.id === u.user_id;
                      return (
                        <button key={u.user_id} type="button"
                          onClick={() => { setSelectedAssignee({ id: u.user_id, label }); setAssignmentMode('user'); setAssigneeSearch(''); setShowAssignPanel(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 min-h-11 text-left border-b border-ui-border last:border-b-0 transition-colors ${isSel ? 'bg-accent-soft/30' : 'hover:bg-ui-subtle'}`}>
                          <div className="w-7 h-7 rounded-full bg-ui-hover flex items-center justify-center text-ui-text2 text-xs font-bold flex-shrink-0">
                            {label[0].toUpperCase()}
                          </div>
                          <span className="text-sm text-ui-text flex-1 truncate">{label}</span>
                          {isSel && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-accent-text flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {assignTab === 'role' && (
              <div>
                <div className="flex items-center gap-2 px-3 py-2 border-b border-ui-border">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ui-text3 flex-shrink-0">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input value={roleSearch} onChange={(e) => setRoleSearch(e.target.value)}
                    placeholder="Search roles…"
                    className="flex-1 text-sm text-ui-text bg-transparent outline-none placeholder:text-ui-text3" />
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {(rolesData?.data ?? [])
                    .filter(r => r.name.toLowerCase().includes(roleSearch.toLowerCase()))
                    .map((r) => {
                      const isSel = queueRoleId === r.id;
                      return (
                        <button key={r.id} type="button"
                          onClick={() => { setQueueRoleId(r.id); setQueueRoleName(r.name); setAssignmentMode('queue'); setShowAssignPanel(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 min-h-11 text-left border-b border-ui-border last:border-b-0 transition-colors ${isSel ? 'bg-accent-soft/30' : 'hover:bg-ui-subtle'}`}>
                          <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent-text"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-ui-text truncate">{r.name}</p>
                            {r.description && <p className="text-xs text-ui-text3 truncate">{r.description}</p>}
                          </div>
                          {isSel && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-accent-text flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Link to ── */}
      <div>
        <label className={labelCls}>Link to</label>

        {/* Selected item — full-width detail card */}
        {contextType && contextEntityId && (() => {
          const mtg = contextType === 'meeting' ? (meetingsData ?? []).find(m => m.meeting_id === contextEntityId) : null;
          const iconMap: Record<string, React.ReactElement> = {
            meeting:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
            property:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><path d="M9 21V12h6v9"/></svg>,
            transaction: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>,
            proposal:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>,
            job:         <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
          };
          const subtitleParts: string[] = [];
          if (mtg?.meeting_type) subtitleParts.push(mtg.meeting_type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()));
          if (mtg?.scheduled_at) {
            const d = new Date(mtg.scheduled_at);
            subtitleParts.push(
              d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' +
              d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
            );
          }
          if (mtg?.location && typeof mtg.location === 'string') subtitleParts.push(mtg.location);
          if (!mtg && contextEntityId) subtitleParts.push(`ID: ${contextEntityId}`);

          return (
            <div className="w-full rounded-xl border border-accent/30 bg-accent-soft/20 px-3.5 py-2.5 flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 text-accent-text">
                {iconMap[contextType] ?? iconMap.proposal}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ui-text leading-tight truncate">{contextLabel || contextEntityId}</p>
                {subtitleParts.length > 0 && (
                  <p className="text-xs text-ui-text3 truncate mt-0.5">{subtitleParts.join(' · ')}</p>
                )}
              </div>
              <button type="button"
                onClick={() => { setContextEntityId(''); setContextLabel(''); setContextType(''); setMeetingSearch(''); setPropertySearch(''); }}
                className="flex-shrink-0 text-accent-text text-xs font-medium hover:opacity-70 transition-opacity">
                change
              </button>
            </div>
          );
        })()}

        {/* Category chips — shown only when nothing is selected */}
        {!contextType && (
          <div className="flex flex-wrap gap-1.5">
            {CONTEXT_TYPES.map((ct) => (
              <button key={ct} type="button"
                onClick={() => { setContextType(ct); setContextEntityId(''); setContextLabel(''); setMeetingSearch(''); setPropertySearch(''); }}
                className="px-3 py-1.5 rounded-full border border-ui-border bg-ui-subtle text-xs font-medium text-ui-text2 hover:border-ui-border transition-colors">
                {ct.charAt(0).toUpperCase() + ct.slice(1)}
              </button>
            ))}
          </div>
        )}

        {contextType === 'meeting' && !contextEntityId && (
          <div className="mt-2 rounded-xl border border-ui-border overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-ui-border">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ui-text3 flex-shrink-0">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input value={meetingSearch} onChange={(e) => setMeetingSearch(e.target.value)}
                placeholder="Search meetings…"
                className="flex-1 text-sm text-ui-text bg-transparent outline-none placeholder:text-ui-text3" />
              <button type="button"
                onClick={() => { setContextType(''); setMeetingSearch(''); }}
                className="flex-shrink-0 text-ui-text3 hover:text-ui-text transition-colors p-0.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto">
              {(meetingsData ?? []).length === 0 ? (
                <p className="px-3 py-4 text-sm text-ui-text3 text-center">No meetings found.</p>
              ) : (
                (meetingsData ?? [])
                  .filter(m => m.title?.toLowerCase().includes(meetingSearch.toLowerCase()))
                  .map((m) => (
                    <button key={m.meeting_id} type="button"
                      onClick={() => { setContextEntityId(m.meeting_id); setContextLabel(m.title || m.meeting_id); setMeetingSearch(''); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 min-h-11 text-left border-b border-ui-border last:border-b-0 hover:bg-ui-subtle transition-colors">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent-text flex-shrink-0">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <span className="text-sm text-ui-text truncate">{m.title || m.meeting_id}</span>
                    </button>
                  ))
              )}
            </div>
          </div>
        )}

        {contextType === 'property' && !contextEntityId && (
          <div className="mt-2 rounded-xl border border-ui-border overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-ui-border">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ui-text3 flex-shrink-0">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input value={propertySearch} onChange={(e) => setPropertySearch(e.target.value)}
                placeholder="Search properties…"
                className="flex-1 text-sm text-ui-text bg-transparent outline-none placeholder:text-ui-text3" />
              <button type="button"
                onClick={() => { setContextType(''); setPropertySearch(''); }}
                className="flex-shrink-0 text-ui-text3 hover:text-ui-text transition-colors p-0.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto">
              {(propertiesData ?? []).length === 0 ? (
                <p className="px-3 py-4 text-sm text-ui-text3 text-center">No properties found.</p>
              ) : (
                (propertiesData ?? []).map((p) => (
                  <button key={p.property_id} type="button"
                    onClick={() => {
                      const streetLine = [
                        p.address?.street_number, p.address?.pre_direction, p.address?.street_name,
                        p.address?.street_suffix, p.address?.post_direction,
                      ].filter(Boolean).join(' ');
                      setContextEntityId(p.property_id);
                      setContextLabel(p.address?.normalized_address || streetLine || p.property_id);
                      setPropertySearch('');
                    }}
                    className="w-full flex items-start gap-2.5 px-3 py-2.5 min-h-11 text-left border-b border-ui-border last:border-b-0 hover:bg-ui-subtle transition-colors">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent-text flex-shrink-0 mt-0.5">
                      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><path d="M9 21V12h6v9"/>
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-ui-text truncate">
                        {[
                          p.address?.street_number, p.address?.pre_direction, p.address?.street_name,
                          p.address?.street_suffix, p.address?.post_direction,
                        ].filter(Boolean).join(' ')}
                      </p>
                      <p className="text-xs text-ui-text3 truncate">{[p.address?.city, p.address?.state].filter(Boolean).join(', ')}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Tags ── */}
      <div>
        <label className={labelCls}>Tags</label>
        <div className="flex flex-wrap gap-1.5 items-center">
          {tags.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-soft/40 border border-accent-soft text-xs font-medium text-accent-text">
              {tag}
              <button type="button" onClick={() => setTags(tags.filter((t2) => t2 !== tag))}
                className="text-accent-text/60 hover:text-accent-text leading-none ml-0.5">×</button>
            </span>
          ))}
          <div className="flex items-center gap-1.5 rounded-full border border-dashed border-ui-border pr-1 pl-2.5 py-1">
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
              placeholder="Add tag…"
              className="text-xs text-ui-text bg-transparent outline-none w-20 placeholder:text-ui-text3"
            />
            <button type="button" onClick={handleAddTag}
              className="w-5 h-5 rounded-full btn-sheen flex items-center justify-center text-on-primary flex-shrink-0">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
          </div>
        </div>
      </div>

      {!hideActions && (
        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Creating...' : (submitLabel || t('tasks.create', 'Create Task'))}
          </Button>
          <Button type="button" variant="outline" onClick={() => (onCancel ? onCancel() : navigate('/tasks'))}>
            {t('common.cancel', 'Cancel')}
          </Button>
        </div>
      )}
    </form>
  );
};

export default TaskForm;
